import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const FilterSidebar = ({ flights = [], filters, onFilterChange }) => {
  // Safe state holder for applied filters
  const safe = {
    departureTimes: filters?.departureTimes || [],
    stops: filters?.stops || [],
    airlines: filters?.airlines || [],
    fareType: filters?.fareType || [],
  };

  const [stopsList, setStopsList] = useState([]);
  const [airlinesList, setAirlinesList] = useState([]);
  const [fareTypeList, setFareTypeList] = useState([]);

  const location = useLocation();
  const searchData = location.state || {};
  const data = searchData.searchParams || searchData;

  // -------------------------------------------------------------------
  //  TOGGLE MULTIPLE SELECTION CHECKBOX
  // -------------------------------------------------------------------
  const handleChange = (filterType, value) => {
    const prev = safe[filterType] || [];

    const updated = prev.includes(value)
      ? prev.filter((v) => v !== value) // remove if exists
      : [...prev, value]; // add if not

    onFilterChange({ [filterType]: updated });
  };

  // -------------------------------------------------------------------
  //  Generate Dynamic Filters When Flights Change
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!Array.isArray(flights) || flights.length === 0) return;

    // Stop List
    const stops = [
      ...new Set(
        flights.map((f) =>
          String(f?.stops ?? f?.totalStops ?? f?.segments?.[0]?.stop ?? 0)
        )
      ),
    ].sort();
    setStopsList(stops);

    // Airlines List
    const airlines = [
      ...new Set(
        flights
          .map((f) => {
            const seg = f?.segments?.[0];
            return (
              seg?.airLineName ||
              seg?.carrierName ||
              seg?.airlineName ||
              f?.airlineName ||
              null
            );
          })
          .filter(Boolean)
      ),
    ].sort();
    setAirlinesList(airlines);

    // Fare Type List
    const fareTypes = [
      ...new Set(
        flights.map((f) =>
          f?.fares?.[0]?.isRefundable ? "Refundable" : "Non-Refundable"
        )
      ),
    ];
    setFareTypeList(fareTypes);
  }, [flights]);

  // -------------------------------------------------------------------
  //  Get Dynamic Departure Time Buckets
  // -------------------------------------------------------------------
  const getAvailableDepartureTimeSlots = () => {
    if (!Array.isArray(flights)) return [];

    const allTimes = flights
      .map((f) => f?.segments?.[0]?.departDateTime || null)
      .filter(Boolean)
      .map((dt) => {
        const d = new Date(dt);
        return isNaN(d) ? null : d.getHours();
      })
      .filter((h) => h !== null);

    if (allTimes.length === 0) return [];

    const timeCategories = {
      morning: [5, 11],
      afternoon: [12, 17],
      evening: [18, 20],
      night: [21, 23],
      lateNight: [0, 4],
    };

    return Object.keys(timeCategories).filter((key) =>
      allTimes.some(
        (h) =>
          h >= timeCategories[key][0] && h <= timeCategories[key][1]
      )
    );
  };

  const departureTimeSlots = getAvailableDepartureTimeSlots();

  // -------------------------------------------------------------------
  // JSX RENDER
  // -------------------------------------------------------------------
  return (
    <div className="box_shadow">
      <div className="accordion" id="filterAccordion">

        {/* Departure Time */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="heading1">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapse1"
            >
              Departure Time
            </button>
          </h2>

          <div id="collapse1" className="accordion-collapse collapse">
            <div className="accordion-body">
              {departureTimeSlots.length === 0 ? (
                <p>No departure time data</p>
              ) : (
                departureTimeSlots.map((cat) => (
                  <div className="form-check w-100" key={cat}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                       id={`departure-${cat}`} 
                      checked={safe.departureTimes.includes(cat)}
                      onChange={() => handleChange("departureTimes", cat)}
                    />
                    <label className="form-check-label text-capitalize" htmlFor={`departure-${cat}`}>
                      {cat === "lateNight" ? "Late Night" : cat}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Stops */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="heading2">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapse2"
            >
              Flight Stops
            </button>
          </h2>

          <div id="collapse2" className="accordion-collapse collapse">
            <div className="accordion-body">
              {stopsList.length === 0 ? (
                <p>No stop data</p>
              ) : (
                stopsList.map((stop) => (
                  <div className="form-check w-100" key={stop}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`departure-${stop}`} 
                      checked={safe.stops.includes(String(stop))}
                      onChange={() => handleChange("stops", String(stop))}
                    />
                    <label className="form-check-label" htmlFor={`departure-${stop}`}>
                      {stop === "0" ? "Non Stop" : `${stop} Stop`}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Airlines */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="heading3">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapse3"
            >
              Airlines
            </button>
          </h2>

          <div id="collapse3" className="accordion-collapse collapse">
            <div className="accordion-body">
              {airlinesList.length === 0 ? (
                <p>No airline data</p>
              ) : (
                airlinesList.map((airline) => (
                  <div className="form-check w-100" key={airline}>
                    <input
                      className="form-check-input"
                      id={`departure-${airline}`} 
                      type="checkbox"
                      checked={safe.airlines.includes(airline)}
                      onChange={() => handleChange("airlines", airline)}
                    />
                    <label className="form-check-label" htmlFor={`departure-${airline}`}>{airline}</label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Fare Type */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="heading4">
            <button
              className="accordion-button fs-5 fw-semibold collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapse4"
            >
              Fare Type
            </button>
          </h2>

          <div id="collapse4" className="accordion-collapse collapse">
            <div className="accordion-body">
              {fareTypeList.length === 0 ? (
                <p>No fare type data</p>
              ) : (
                fareTypeList.map((fare) => (
                  <div className="form-check w-100" key={fare}>
                    <input
                      className="form-check-input"
                      id={`departure-${fare}`} 
                      type="checkbox"
                      checked={safe.fareType.includes(fare)}
                      onChange={() => handleChange("fareType", fare)}
                    />
                    <label className="form-check-label" htmlFor={`departure-${fare}`}>{fare}</label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FilterSidebar;
